/**
 * User Data Loader
 * 
 * Handles loading of user-specific data including profile, referral data,
 * and static assets with referral stats caching and asset management.
 */

import { supabase } from '@/integrations/supabase/client';
import { indexedDBService } from '../../indexeddb';
import { UserProfileTransformer } from '../DataTransformer';
import { BaseLoader, type ExtendedLoaderResult } from './BaseLoader';
import { STORES } from '../../indexeddb/schema';

/**
 * User Data Loader Implementation
 * 
 * Specializes in loading user-specific data including profile information,
 * referral codes and data, and static assets like logos.
 */
export class UserDataLoader extends BaseLoader {
  /**
   * Load all user-specific data
   */
  async load(userId: string): Promise<ExtendedLoaderResult> {
    const errors: string[] = [];
    let userProfileCount = 0;
    let referralCodesCount = 0;
    let referralsCount = 0;
    let staticAssetsCount = 0;

    try {
      // Load user profile if enabled
      if (this.config.includeUserProfile) {
        const profileResult = await this.loadUserProfile(userId);
        userProfileCount = profileResult.count;
        errors.push(...profileResult.errors);
      }

      // Load referral data if enabled
      if (this.config.includeReferralData) {
        const referralResult = await this.loadReferralData(userId);
        referralCodesCount = referralResult.codesCount;
        referralsCount = referralResult.referralsCount;
        errors.push(...referralResult.errors);
      }

      // Load static assets if enabled
      if (this.config.includeStaticAssets && this.config.includeLogos) {
        const assetsResult = await this.loadStaticAssets();
        staticAssetsCount = assetsResult.count;
        errors.push(...assetsResult.errors);
      }

      return this.createExtendedLoaderResult({
        userProfile: userProfileCount,
        referralCodes: referralCodesCount,
        referrals: referralsCount,
        staticAssets: staticAssetsCount
      }, errors);
    } catch (error) {
      const errorMessage = this.handleError(error, 'User data loading failed');
      return this.createExtendedLoaderResult({
        userProfile: 0,
        referralCodes: 0,
        referrals: 0,
        staticAssets: 0
      }, [errorMessage]);
    }
  }

  /**
   * Load user profile data
   */
  async loadUserProfile(userId: string): Promise<{ count: number; errors: string[] }> {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        return { count: 0, errors: [error.message] };
      }

      if (data) {
        const offlineProfile = UserProfileTransformer.fromSupabase(data);
        await indexedDBService.offlineDB.put(STORES.USER_PROFILE, offlineProfile);
        return { count: 1, errors: [] };
      }

      return { count: 0, errors: [] };
    } catch (error) {
      const errorMessage = this.handleError(error, 'User profile loading failed');
      return { count: 0, errors: [errorMessage] };
    }
  }

  /**
   * Load referral data (codes and referrals)
   */
  async loadReferralData(userId: string): Promise<{ codesCount: number; referralsCount: number; errors: string[] }> {
    const errors: string[] = [];
    let codesCount = 0;
    let referralsCount = 0;

    try {
      // Load user's referral code
      const { data: codeData, error: codeError } = await supabase
        .from('referral_codes')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (codeError) {
        console.error('❌ Error loading referral code:', codeError.message);
        errors.push(`Referral codes: ${codeError.message}`);
      } else if (codeData) {
        const { referralCodeOps } = await import('../../indexeddb/operations');
        await referralCodeOps.cacheFromAPI(codeData);
        codesCount = 1;
      }

      // Load user's referrals (as referrer)
      const { data: referralData, error: referralError } = await supabase
        .from('referrals')
        .select('*')
        .eq('referrer_id', userId)
        .order('created_at', { ascending: false });

      if (referralError) {
        console.error('❌ Error loading referrals:', referralError.message);
        errors.push(`Referrals: ${referralError.message}`);
      } else if (referralData && referralData.length > 0) {
        const { referralOps } = await import('../../indexeddb/operations');
        await referralOps.bulkCacheFromAPI(referralData);
        referralsCount = referralData.length;
      }

      // Also compute and cache referral stats for offline use
      if (codesCount > 0 || referralsCount > 0) {
        try {
          await this.cacheReferralStats(userId, referralData || []);
        } catch (statsError) {
          console.warn('Failed to cache referral stats:', statsError);
          errors.push(`Referral stats caching failed: ${statsError instanceof Error ? statsError.message : 'Unknown error'}`);
        }
      }

      return { codesCount, referralsCount, errors };

    } catch (error) {
      const errorMessage = this.handleError(error, 'Referral data loading failed');
      return { codesCount: 0, referralsCount: 0, errors: [errorMessage] };
    }
  }

  /**
   * Cache computed referral stats for offline use
   */
  private async cacheReferralStats(userId: string, referralData: Record<string, unknown>[]): Promise<void> {
    const REWARD_THRESHOLD = 3;

    // Calculate stats from referral data
    const totalReferrals = referralData.length;
    const successfulSignups = referralData.filter(r => r.status === 'signed_up' || r.status === 'converted').length;
    const premiumConversions = referralData.filter(r => r.status === 'converted').length;
    const rewardsEarned = Math.floor(premiumConversions / REWARD_THRESHOLD);
    const progressToNextReward = premiumConversions % REWARD_THRESHOLD;

    // Get recent referrals (last 5)
    const recentReferrals = referralData
      .sort((a, b) => new Date(b.created_at as string).getTime() - new Date(a.created_at as string).getTime())
      .slice(0, 5)
      .map(r => ({
        id: (r.id as string) || crypto.randomUUID(),
        created_at: r.created_at as string,
        status: r.status as string
      }));

    // Cache the stats in IndexedDB
    const { referralStatsOps } = await import('../../indexeddb/operations');
    await referralStatsOps.cacheStats(userId, {
      total_referrals: totalReferrals.toString(),
      successful_signups: successfulSignups.toString(),
      premium_conversions: premiumConversions.toString(),
      rewards_earned: rewardsEarned.toString(),
      progress_to_next_reward: progressToNextReward,
      recent_referrals: recentReferrals
    }, 24 * 60 * 60 * 1000); // Cache for 24 hours
  }

  /**
   * Load static assets (logos for authenticated app)
   */
  async loadStaticAssets(): Promise<{ count: number; errors: string[] }> {
    try {
      // Import the logo asset manager
      const { logoAssetManager } = await import('../../assets/LogoAssetManager');

      // Cache all logos for authenticated app areas
      const result = await logoAssetManager.cacheAllLogos();

      if (result.success) {
        return { count: 2, errors: [] }; // 2 logos cached
      } else {
        console.warn('⚠️ Some logos failed to cache:', result.errors);
        return { count: 0, errors: result.errors };
      }
    } catch (error) {
      const errorMessage = this.handleError(error, 'Static assets loading failed');
      return { count: 0, errors: [errorMessage] };
    }
  }

  /**
   * Load user preferences
   */
  async loadUserPreferences(userId: string): Promise<{ count: number; errors: string[] }> {
    try {
      if (!this.config.includeUserPreferences) {
        return { count: 0, errors: [] };
      }

      // User preferences are typically stored in the profiles table
      // or a separate user_preferences table if it exists
      const { data, error } = await supabase
        .from('profiles')
        .select('preferences')
        .eq('id', userId)
        .single();

      if (error) {
        return { count: 0, errors: [error.message] };
      }

      if (data && data.preferences) {
        // Cache preferences in IndexedDB if needed
        // This would depend on your specific preferences structure
        return { count: 1, errors: [] };
      }

      return { count: 0, errors: [] };
    } catch (error) {
      const errorMessage = this.handleError(error, 'User preferences loading failed');
      return { count: 0, errors: [errorMessage] };
    }
  }

  /**
   * Get user data statistics
   */
  async getUserStats(userId: string): Promise<{
    hasProfile: boolean;
    referralCode: string | null;
    totalReferrals: number;
    successfulReferrals: number;
    assetsLoaded: boolean;
  }> {
    try {
      const profile = await indexedDBService.userProfile.getCurrentProfile(userId);
      const referralCode = await indexedDBService.referralCodes.getByUserId(userId);
      const referrals = await indexedDBService.referrals.getByReferrerId(userId);
      
      const hasProfile = !!profile;
      const totalReferrals = referrals.length;
      const successfulReferrals = referrals.filter(r => 
        r.status === 'signed_up' || r.status === 'converted'
      ).length;

      return {
        hasProfile,
        referralCode: referralCode?.code || null,
        totalReferrals,
        successfulReferrals,
        assetsLoaded: true // Assume assets are loaded if we got this far
      };
    } catch (error) {
      console.warn('Failed to calculate user stats:', error);
      return {
        hasProfile: false,
        referralCode: null,
        totalReferrals: 0,
        successfulReferrals: 0,
        assetsLoaded: false
      };
    }
  }
}

// Export singleton instance
export const userDataLoader = new UserDataLoader();
